import type { AxiosResponse } from 'axios'
import axios from 'axios'
import type { DateTime } from 'luxon'

import environment from '@/environment'

export const PRODUCTION_PLANS_API_URL = environment.services.productionPlanApiUrl

/**
 * Calls msa-production-plan-manager to export the production plan CSV for the given date
 * and mark the exported plans as MANUALLY_PUBLISHED
 *
 * @param date - Desired production plan date. The backend expects an ISO date string (yyyy-MM-dd).
 * @returns Object containing the CSV Blob and the filename parsed from the Content-Disposition header (if present).
 */
export async function exportAndManuallyPublishProductionPlan(
  date: DateTime,
): Promise<{ blob: Blob; filename?: string }> {
  let response: AxiosResponse<Blob>
  try {
    response = await postForCsv('/export-and-manually-publish', date)
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      //TODO temporary fallback to the old /export endpoint in case the new one has not been deployed yet
      response = await postForCsv('/export', date)
    } else {
      throw error
    }
  }

  const filename = parseFilename(response)
  return { blob: response.data, filename }
}

function postForCsv(path: string, date: DateTime): Promise<AxiosResponse<Blob>> {
  const dateString = date.toISODate() // yyyy-MM-dd

  return axios.post<Blob>(`${PRODUCTION_PLANS_API_URL}${path}`, undefined, {
    params: { date: dateString },
    responseType: 'blob',
    headers: {
      Accept: 'text/csv',
    },
  })
}

function parseFilename(response: AxiosResponse): string | undefined {
  const contentDisposition = response.headers['content-disposition'] as string | undefined
  let filename: string | undefined

  if (contentDisposition) {
    const parts = contentDisposition.split('filename=')
    if (parts.length > 1) {
      const after = parts[1]
      const raw = after.split(';')[0].trim()
      filename = raw.replace(/^"(.*)"$/, '$1')
    }
  }

  return filename
}
